'use client';

import { createContext, useContext, useMemo, ReactNode } from 'react';
import { Tables } from '@/types_db';
import usePollingQuery from '@/utils/hooks/usePollingQuery';
import { createClient } from '@/utils/supabase/client';
import { liveTrainingFields } from '@/utils/supabase/queries';

type Training = Tables<'trainings'>;

interface ContextValue {
  map: Map<number, Partial<Training>>;
  list: Partial<Training>[];
}

const TrainingsLiveContext = createContext<ContextValue>({
  map: new Map(),
  list: [],
});

interface Props {
  ssrTrainings: Partial<Training>[];
  children: ReactNode;
}

export function TrainingsProvider({ ssrTrainings, children }: Props) {
  const supabase = createClient();
  const ids = useMemo(
    () => ssrTrainings.map((t) => t.id).filter((id): id is number => typeof id === 'number'),
    [ssrTrainings]
  );
  const hasActive = useMemo(
    () => ssrTrainings.some((t) => !t.is_success && !t.is_error),
    [ssrTrainings]
  );

  const { data: wsTrainings } = usePollingQuery<'trainings'>({
    supabase,
    table: 'trainings',
    select: liveTrainingFields,
    filter: ids.length ? ['id', 'in', ids] : undefined,
    enabled: ids.length > 0 && hasActive,
    stopWhen: (rows) =>
      rows.length >= ids.length && rows.every((r) => r.is_success || r.is_error),
  });

  const value = useMemo<ContextValue>(() => {
    const map = new Map<number, Partial<Training>>();
    ssrTrainings.forEach((t) => {
      if (typeof t.id === 'number') map.set(t.id, t);
    });
    wsTrainings.forEach((r) => {
      if (typeof r.id === 'number') map.set(r.id, r);
    });
    return { map, list: Array.from(map.values()) };
  }, [ssrTrainings, wsTrainings]);

  return <TrainingsLiveContext.Provider value={value}>{children}</TrainingsLiveContext.Provider>;
}

export function useTrainingLive(id: number | undefined): Partial<Training> | undefined {
  const { map } = useContext(TrainingsLiveContext);
  return typeof id === 'number' ? map.get(id) : undefined;
}

export function useTrainingsLive(): Partial<Training>[] {
  return useContext(TrainingsLiveContext).list;
}
